import React from 'react';

import { OmitStrict } from '@tanium/coreui-utils';
import styled from '@tanium/react-emotion-9';
import { useRandomId } from '@tanium/react-use-random-id';
import { css } from '@tanium/style-system';

import Radio, { RADIOBUTTON_SIZE, Props as RadioProps } from './Radio';
import { Label } from './themeDefinitions';

interface LabelProps {
  /**
   * This id pairs the label with the input element. If not provided, the component generates its
   * own ID to use.
   */
  id?: string;
  /**
   * The text or markup that is shown in the label.
   */
  label: React.ReactNode;
}

export interface Props extends OmitStrict<RadioProps, 'id'>, LabelProps {}

/**
 * Styles the label element's text and aligns it appropriately with the radio button.
 */
const LabelWrapper = styled('label', { shouldForwardProp: (p) => p !== 'disabled' })<{
  disabled: boolean;
}>(
  css({
    color: Label,
    float: 'left',
    width: `calc(100% - ${RADIOBUTTON_SIZE}px)`,
    marginTop: '-2px',
    paddingLeft: 1,
    fontSize: 14,
    lineHeight: '20px',
    // Override global styles from third parties such as bootstrap
    fontWeight: 'normal',
    marginBottom: 0,
  }),
  ({ disabled }) => [disabled && css({ pointerEvents: 'none' })],
);

const LeftFloat = styled('span')({
  float: 'left',
  lineHeight: 1,
});

const ClearFloat = styled('div')({ clear: 'both' });

/**
 * This component pairs the Radio component with a corresponding label. The width of this component,
 * including label, is determined by the container that this component is placed into. Label text
 * wraps to the next line when necessary.
 */
const RadioWithLabel = ({ id, label, disabled, ...others }: Props) => {
  const autoGeneratedInputId = `radiobutton-${useRandomId()}`;

  // consumer can optionally override the id that links label and Radio
  const inputId = id || autoGeneratedInputId;
  return (
    <>
      <LeftFloat>
        <Radio id={inputId} disabled={disabled} {...others} />
      </LeftFloat>
      <LabelWrapper htmlFor={inputId} disabled={!!disabled}>
        {label}
      </LabelWrapper>
      <ClearFloat />
    </>
  );
};

export default RadioWithLabel;
